/**
 * SemenAnalysisCard — iter1060 — спермограма спрямо долните референтни
 * граници на СЗО плюс 74-дневният прозорец, който обяснява резултата.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { ArrowRight, ChevronDown, Clock, FlaskConical, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  below: "text-rose-500",
  borderline: "text-amber-500",
  ok: "text-emerald-500",
  no_data: "opacity-40",
};

const WINDOW_TONE = {
  high: { light: "bg-rose-50 border-rose-300", dark: "bg-rose-900/25 border-rose-500/40" },
  moderate: { light: "bg-amber-50 border-amber-200", dark: "bg-amber-900/20 border-amber-500/30" },
  low: { light: "bg-emerald-50 border-emerald-200", dark: "bg-emerald-900/20 border-emerald-500/30" },
  no_data: { light: "bg-slate-50 border-slate-200", dark: "bg-slate-800/40 border-slate-700" },
};

export const SemenAnalysisCard = ({ isDark }) => {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [vals, setVals] = useState({});
  const [exp, setExp] = useState([]);
  const [busy, setBusy] = useState(false);
  const [why, setWhy] = useState(null);

  useEffect(() => {
    axios.get(`${API}/semen-analysis`)
      .then(({ data }) => {
        setData(data);
        setVals(Object.fromEntries(Object.entries(data.values || {}).map(([k, v]) => [k, String(v)])));
        setExp(data.window?.selected || []);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [API]);

  const save = async () => {
    setBusy(true);
    try {
      const numeric = {};
      Object.entries(vals).forEach(([k, v]) => { if (v !== "" && v != null) numeric[k] = parseFloat(v); });
      const { data: d } = await axios.post(`${API}/semen-analysis`, { values: numeric, exposures: exp });
      setData(d);
      toast.success(d.evaluate?.label_bg || "Записано.");
    } catch { toast.error("Записът не беше запазен."); }
    finally { setBusy(false); }
  };

  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const box = isDark ? "bg-slate-800/50" : "bg-slate-100";
  const inputCls = `px-2 py-1.5 rounded-md border text-sm w-full ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`;

  if (loading) {
    return <div className={`p-5 rounded-2xl border ${cardCls} flex justify-center`} data-testid="semen-loading">
      <Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div>;
  }
  if (!data) return null;
  const ev = data.evaluate, win = data.window;
  const wt = WINDOW_TONE[win.level] || WINDOW_TONE.no_data;
  const allParams = [...(data.params || []), ...(data.reverse_params || [])];

  const groups = {};
  (win.items || []).forEach((i) => { (groups[i.group_bg] = groups[i.group_bg] || []).push(i); });

  return (
    <div className={`p-5 rounded-2xl border-2 ${cardCls}`} data-testid="semen-analysis-card">
      <button onClick={() => setOpen(!open)} className="w-full text-left flex items-center gap-2"
              data-testid="semen-toggle">
        <FlaskConical className="w-4 h-4 text-indigo-500 shrink-0" />
        <h3 className="font-bold text-sm flex-1 min-w-0">Спермограма</h3>
        {data.has_data && (
          <span className="text-[10px] px-2 py-1 rounded-md font-bold bg-indigo-600 text-white shrink-0 max-w-[45%] truncate"
                data-testid="semen-badge">{ev.label_bg}</span>
        )}
        <ChevronDown className={`w-4 h-4 opacity-50 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <p className="text-[11px] opacity-75 leading-relaxed mt-1" data-testid="semen-control">{data.control_bg}</p>

      {open && (
        <div className="mt-4 space-y-4">
          {/* въвеждане */}
          <div className={`p-2.5 rounded-xl ${box}`} data-testid="semen-form">
            <div className="text-[11px] font-bold mb-1.5">Въведете стойностите от резултата</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {allParams.map((p) => (
                <label key={p.key} className="flex flex-col gap-1">
                  <span className="text-[10px] opacity-70 leading-tight">
                    {p.label_bg} {p.unit ? `(${p.unit})` : ""}
                  </span>
                  <input type="number" step="0.1" className={inputCls} value={vals[p.key] ?? ""}
                         data-testid={`semen-input-${p.key}`}
                         onChange={(e) => setVals({ ...vals, [p.key]: e.target.value })} />
                  <span className="text-[9px] opacity-50">
                    {p.reverse ? `под ${p.limit}` : `граница ${p.limit}`}
                  </span>
                </label>
              ))}
            </div>
            <button onClick={save} disabled={busy} data-testid="semen-save"
                    className="w-full mt-2 py-2 rounded-lg bg-indigo-600 text-white text-[11px] font-bold disabled:opacity-60">
              {busy ? "Разчитам…" : "Разчетете резултата"}
            </button>
          </div>

          {/* разчитане */}
          {data.has_data && (
            <div className={`p-3 rounded-xl border-l-4 border-indigo-400 ${box}`} data-testid="semen-verdict">
              <div className="text-xs font-bold" data-testid="semen-verdict-label">{ev.label_bg}</div>
              <div className="text-[11px] opacity-85 mt-1 leading-relaxed">{ev.text_bg}</div>
              <div className="mt-2 space-y-1" data-testid="semen-rows">
                {ev.rows.filter((r) => r.status !== "no_data").map((r) => (
                  <div key={r.key} className="flex items-baseline gap-2">
                    <span className="text-[10px] flex-1 truncate">{r.label_bg}</span>
                    <span className={`text-[11px] font-bold tabular-nums shrink-0 ${STATUS_TONE[r.status]}`}
                          data-testid={`semen-row-${r.key}`}>
                      {r.value} {r.unit}
                    </span>
                    <span className="text-[9px] opacity-50 w-12 text-right shrink-0">
                      {r.reverse ? `< ${r.limit}` : `≥ ${r.limit}`}
                    </span>
                  </div>
                ))}
              </div>
              {ev.total_note_bg && (
                <div className="text-[10px] opacity-75 mt-1.5" data-testid="semen-total-note">{ev.total_note_bg}</div>
              )}
              {ev.obstruction_bg && (
                <div className="text-[10px] opacity-85 mt-1.5 leading-relaxed" data-testid="semen-obstruction">
                  {ev.obstruction_bg}
                </div>
              )}
              <details className="mt-2">
                <summary className="text-[10px] opacity-60 cursor-pointer" data-testid="semen-why-summary">
                  Защо точно тези граници?
                </summary>
                <div className="mt-1 space-y-1">
                  {ev.rows.map((r) => (
                    <div key={`why-${r.key}`} className="text-[10px] opacity-75 leading-relaxed">
                      <b>{r.label_bg}:</b> {r.why_bg}
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}

          {/* 74-дневният прозорец */}
          <div>
            <div className="text-[11px] font-bold mb-1.5 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5 text-indigo-500" />
              74-дневният прозорец — какво обяснява ДНЕШНИЯ резултат
            </div>
            {Object.entries(groups).map(([g, items]) => (
              <div key={g} className="mb-2">
                <div className="text-[10px] font-bold opacity-70 mb-1">{g}</div>
                <div className="space-y-1">
                  {items.map((i) => (
                    <div key={i.key} className={`rounded-xl ${box}`}>
                      <div className="p-2 flex items-start gap-2">
                        <button onClick={() => setExp(exp.includes(i.key)
                                  ? exp.filter((k) => k !== i.key) : [...exp, i.key])}
                                data-testid={`semen-exp-${i.key}`} role="checkbox"
                                aria-checked={exp.includes(i.key)}
                                className={`shrink-0 w-4 h-4 mt-0.5 rounded border ${exp.includes(i.key)
                                  ? "bg-indigo-600 border-indigo-600" : isDark ? "border-slate-600" : "border-slate-400"}`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] font-bold leading-snug">{i.label_bg}</div>
                          <button onClick={() => setWhy(why === i.key ? null : i.key)}
                                  className="text-[10px] opacity-50 hover:opacity-100"
                                  data-testid={`semen-why-${i.key}`}>защо?</button>
                          {why === i.key && (
                            <div className="text-[10px] opacity-80 mt-0.5 leading-relaxed">{i.why_bg}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className={`p-3 rounded-xl border ${isDark ? wt.dark : wt.light}`} data-testid="semen-window">
              <div className="flex items-baseline gap-2">
                <span className="text-xs font-bold" data-testid="semen-window-label">{win.label_bg}</span>
                <span className="ml-auto text-[10px] opacity-60 tabular-nums">{win.load} т.</span>
              </div>
              <div className="text-[11px] opacity-85 mt-1 leading-relaxed">{win.text_bg}</div>
              {win.priority_bg?.length > 0 && (
                <ul className="text-[10px] opacity-85 mt-1.5 space-y-0.5" data-testid="semen-window-priority">
                  {win.priority_bg.map((p, i) => <li key={i}>• {p}</li>)}
                </ul>
              )}
            </div>
          </div>

          <div className={`p-3 rounded-xl ${box}`} data-testid="semen-rules">
            <div className="text-[11px] font-bold">Как се взема пробата (иначе числото е невярно)</div>
            <ul className="text-[10px] opacity-85 mt-1 space-y-0.5">
              {data.collection_rules_bg.map((r, i) => <li key={i}>• {r}</li>)}
            </ul>
          </div>

          <p className="text-[10px] opacity-60 leading-relaxed" data-testid="semen-disclaimer">
            {data.disclaimer_bg}
          </p>

          <div className="flex flex-wrap gap-1.5" data-testid="semen-links">
            {data.links.map((l, i) => (
              <button key={i} onClick={() => navigate(l.route)} data-testid={`semen-link-${i}`}
                      className={`text-[10px] px-2 py-1 rounded-lg border flex items-center gap-1 ${isDark ? "border-slate-700" : "border-slate-300"}`}>
                {l.twin_bg} <ArrowRight className="w-3 h-3" /> <span className="opacity-70">{l.why_bg}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SemenAnalysisCard;
